import {
  adminMealPatternPreviewSchema,
  adminMealPatternSchema,
  type AdminMealPattern,
  type AdminMealPatternActivateRequest,
  type AdminMealPatternCreateRequest,
  type AdminMealPatternPreview,
  type AdminMealPatternRetireRequest,
  type AdminMealPatternUpdateRequest,
} from '@steado/admin-contract/meal-patterns';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type { ApiClient, QueryValue } from '@/lib/api/client';

import type { MealPatternsSearch } from './meal-patterns-search';

export function mealPatternsQueryOptions(api: ApiClient, search: MealPatternsSearch) {
  const query: Record<string, QueryValue> = {
    slot: search.slot,
    status: search.status,
    history: search.history,
  };

  return queryOptions({
    queryKey: ['admin', 'meal-patterns', search.slot ?? '', search.status ?? '', search.history ?? false],
    queryFn: (): Promise<AdminMealPattern[]> =>
      api.request({ path: '/admin/meal-patterns', query, schema: z.array(adminMealPatternSchema) }),
    placeholderData: (previous) => previous,
  });
}

/**
 * The preview is a POST because it computes rather than reads, so it cannot
 * ride a plain query key that refetches on focus. It is fetched on demand and
 * cached under the version's id, which is immutable — a version's content
 * never changes, so its preview only goes stale when the weeks behind it do.
 */
/**
 * What one slot's whole live grammar rejects.
 *
 * Slot-scoped rather than version-scoped, because that is the question the
 * screen asks: a seeded shape has no row and therefore no id, and a slot's
 * verdict is not the sum of its versions' — a meal has to satisfy all of them
 * at once.
 */
export function previewSlot(api: ApiClient, slot: string): Promise<AdminMealPatternPreview> {
  return api.request({
    path: '/admin/meal-patterns/preview',
    method: 'POST',
    body: { slot },
    schema: adminMealPatternPreviewSchema,
  });
}

export function previewPattern(api: ApiClient, id: string): Promise<AdminMealPatternPreview> {
  return api.request({
    path: `/admin/meal-patterns/${id}/preview`,
    method: 'POST',
    schema: adminMealPatternPreviewSchema,
  });
}

export function createPattern(
  api: ApiClient,
  body: AdminMealPatternCreateRequest,
): Promise<AdminMealPattern> {
  return api.request({
    path: '/admin/meal-patterns',
    method: 'POST',
    body,
    schema: adminMealPatternSchema,
  });
}

/** An edit is the next version, never an update in place. */
export function savePatternVersion(
  api: ApiClient,
  id: string,
  body: AdminMealPatternUpdateRequest,
): Promise<AdminMealPattern> {
  return api.request({
    path: `/admin/meal-patterns/${id}`,
    method: 'PUT',
    body,
    schema: adminMealPatternSchema,
  });
}

export function activatePattern(
  api: ApiClient,
  id: string,
  body: AdminMealPatternActivateRequest,
): Promise<AdminMealPattern> {
  return api.request({
    path: `/admin/meal-patterns/${id}/activate`,
    method: 'POST',
    body,
    schema: adminMealPatternSchema,
  });
}

export function retirePattern(
  api: ApiClient,
  id: string,
  body: AdminMealPatternRetireRequest,
): Promise<AdminMealPattern> {
  return api.request({
    path: `/admin/meal-patterns/${id}/retire`,
    method: 'POST',
    body,
    schema: adminMealPatternSchema,
  });
}
